import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
// LOT 2 : traduction des conflits d'identifiant MQTT. Module SANS dependance
// (cf. services/erreursApi.ts) — l'import doit rester en tete du fichier,
// eslint `import/first` fait echouer le build de production sinon.
import { conflitMqttDepuisErreur, conflitVersionDepuisErreur } from './erreursApi';

// Configuration de base pour axios
const API_BASE_URL = '/api';

// Token storage keys
const TOKEN_KEY = 'auth_token';
const TOKEN_EXPIRY_KEY = 'auth_token_expiry';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 secondes
});

/**
 * Levée par `login()` quand le serveur temporise la connexion (HTTP 429).
 *
 * Sans elle, l'écran de connexion affichait « Échec de l'authentification.
 * Veuillez vérifier vos identifiants. » — message FAUX pendant une
 * temporisation, et surtout contre-productif : l'opérateur retape son mot de
 * passe en boucle et aggrave sa propre temporisation.
 */
export class ErreurTropDeTentatives extends Error {
  readonly secondes: number;

  constructor(secondes: number) {
    super('Trop de tentatives de connexion');
    this.name = 'ErreurTropDeTentatives';
    this.secondes = secondes;
    // Nécessaire pour que `instanceof` fonctionne après transpilation ES5.
    Object.setPrototypeOf(this, ErreurTropDeTentatives.prototype);
  }
}

// LOT 2 — La traduction des conflits d'identifiant MQTT est définie dans un
// module SANS dépendance (services/erreursApi.ts) : ApiService importe axios, publié
// en ESM et non transpilé par le Jest de react-scripts, donc tout test qui
// touche ce fichier fait échouer la suite. On ré-exporte ici pour que les
// imports existants depuis '../services/ApiService' continuent de marcher.
export {
  ErreurConflitMqtt,
  ErreurVersionPerimee,
  conflitMqttDepuisErreur,
  conflitVersionDepuisErreur,
  messageErreurApi,
} from './erreursApi';
export type { CodeConflitMqtt } from './erreursApi';

/**
 * Délai d'attente annoncé par le serveur, en secondes.
 *
 * Deux sources, dans cet ordre : le corps JSON (`retry_after`) puis l'en-tête
 * `Retry-After`. Le corps d'abord parce qu'un en-tête n'est lisible en
 * cross-origin que s'il est exposé par CORS, et qu'un
 * `Access-Control-Expose-Headers: *` n'est pas honoré par les navigateurs sur
 * une requête avec identifiants.
 */
function delaiDeReessai(erreur: any): number {
  const corps = erreur?.response?.data;
  const depuisCorps = Number(corps?.retry_after);
  if (Number.isFinite(depuisCorps) && depuisCorps > 0) {
    return Math.ceil(depuisCorps);
  }
  const entete = erreur?.response?.headers?.['retry-after'];
  const depuisEntete = Number(entete);
  if (Number.isFinite(depuisEntete) && depuisEntete > 0) {
    return Math.ceil(depuisEntete);
  }
  return 60;
}

// Intercepteur pour ajouter le token JWT aux requêtes
api.interceptors.request.use(config => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Callback appelé par l'intercepteur 401 pour notifier le changement d'auth
// Initialisé par ApiService dans son constructeur
let _onAuthErrorCallback: (() => void) | null = null;

// Flag pour éviter les boucles infinies de refresh
let _isRefreshing = false;
// File d'attente des requêtes en attente du refresh
let _refreshSubscribers: Array<(token: string) => void> = [];

function _onRefreshComplete(newToken: string) {
  _refreshSubscribers.forEach(cb => cb(newToken));
  _refreshSubscribers = [];
}

// Intercepteur de réponse : tente un refresh token avant de déconnecter
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Si 401 et que ce n'est pas déjà un retry ou un appel au refresh lui-même
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/token/refresh')
    ) {
      originalRequest._retry = true;

      // Si un refresh est déjà en cours, mettre la requête en file d'attente
      if (_isRefreshing) {
        return new Promise(resolve => {
          _refreshSubscribers.push((newToken: string) => {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      _isRefreshing = true;

      try {
        const oldToken = localStorage.getItem(TOKEN_KEY);
        if (oldToken) {
          const response = await api.post('/token/refresh', null, {
            headers: { 'Authorization': `Bearer ${oldToken}` },
          });
          const newToken = response.data.access_token;

          // Stocker le nouveau token et sa date d'expiration
          localStorage.setItem(TOKEN_KEY, newToken);
          try {
            const payload = JSON.parse(atob(newToken.split('.')[1]));
            if (payload.exp) {
              localStorage.setItem(TOKEN_EXPIRY_KEY, new Date(payload.exp * 1000).toISOString());
            }
          } catch {
            // Fallback expiry
            const expiry = new Date();
            expiry.setMinutes(expiry.getMinutes() + 60);
            localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toISOString());
          }

          _isRefreshing = false;
          _onRefreshComplete(newToken);

          // Rejouer la requête originale avec le nouveau token
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh échoué — déconnexion
        _isRefreshing = false;
        _refreshSubscribers = [];
      }

      // Pas de token ou refresh échoué — nettoyer et notifier
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      if (_onAuthErrorCallback) {
        _onAuthErrorCallback();
      }
    }
    return Promise.reject(error);
  }
);

// Interfaces pour les données
export interface SensorReading {
  ts: string;
  temp?: number;
  pH?: number;
  cond?: number;
  // Support pour champs dynamiques
  [key: string]: number | string | undefined;
}

export interface RelayStatusInfo {
  relay_number: number;
  name: string;
  is_enabled: boolean;
  current_state: boolean;
  field_code?: string;
}

export interface DeviceField {
  field_code: string;
  field_name: string;
  unit?: string;
  precision_digits: number;
  display_order: number;
  icon?: string;
}

export interface BoitierStatus {
  boitier_id: number;
  name?: string;
  location?: string;
  mqtt_topic?: string;
  /** Nom réseau remonté par le boîtier dans ses diagnostics (ex: testMPB3) */
  hostname?: string;
  last_reading: SensorReading | null;
  fields: DeviceField[]; // Champs mesurés par ce device
  online: boolean;
  mqtt_connected: boolean; // Connexion MQTT active (basé sur diagnostics)
  sensors_active: boolean; // Capteurs envoyant des mesures récentes
  last_seen: string | null;
  alerts: string[];
  regulation_enabled: boolean;
  relays: RelayStatusInfo[];
  /** Présence temps réel (battement MQTT) — absente sur firmware antérieur */
  presence?: {
    etat: 'en_ligne' | 'retard' | 'hors_ligne' | 'indetermine' | 'inconnu';
    age_s?: number | null;
    motif?: string | null;
    derniere_vue?: string | null;
  };
}

export interface AlertThreshold {
  min: number;
  max: number;
  isactive: boolean;
}

export interface AlertSettings {
  temp: AlertThreshold;
  pH: AlertThreshold;
  cond: AlertThreshold;
}

export interface HistoryPoint {
  ts: string;
  value: number;
}

// ---- Capteur de niveau ----
export interface LevelReading {
  ts: string;
  niveau: number | null;
  [key: string]: number | string | boolean | null | undefined;
}

export interface LevelStatus {
  id: number;
  name?: string;
  mqtt_topic?: string;
  last_reading: LevelReading | null;
  online: boolean;
  mqtt_connected: boolean; // Connexion MQTT active
  sensor_active: boolean; // Capteur envoyant des mesures
  last_seen: string | null;
  alerts: string[];
  boitier_id?: number;
  fields?: DeviceField[];
}

export interface LevelHistoryData {
  id: number;
  niveau: HistoryPoint[];
  [key: string]: HistoryPoint[] | string | number | undefined;
}

export interface HistoryData {
  boitier_id: number;
  temp?: HistoryPoint[];
  pH?: HistoryPoint[];
  cond?: HistoryPoint[];
  // Support pour champs dynamiques
  [key: string]: HistoryPoint[] | number | undefined;
}

export interface Alert {
  id: number;
  boitier_id: number;
  boitier_nom: string;
  constante: string;
  valeur: string;
  message: string;
  etat: string;
  date_apparition: string;
  date_fin: string | null;
  min_threshold: number | null;
  max_threshold: number | null;
}

/**
 * LOT 7.5 — `number | null` PARTOUT sur les grandeurs mesurees.
 *
 * L'API ne rabat plus l'absence de mesure sur 0 (`_extract_full_diagnostics`
 * n'a plus un seul `, 0) or 0`). Ces champs valent donc `null` quand le
 * firmware n'a rien publie. Le type impose au compilateur de refuser
 * `usage_percent || 0`. C'est par ce raccourci que le defaut du disque a ete
 * recopie d'un composant de mai vers un composant d'aout.
 *
 * `donnees_disponibles` indique bloc par bloc ce que l'API a recu, sans avoir
 * a deviner si un `null` vient d'un firmware ancien ou d'un collecteur en
 * panne.
 */
export interface DiagnosticData {
  timestamp: string;
  boitier_id: number;
  uptime: number | null;
  memory: {
    free: number | null;
    used: number | null;
    total: number | null;
    usage_percent: number | null;
  };
  wifi: {
    status: number | null;
    rssi: number | null;
    reconnect_count?: number | null;
    ip_address?: string | null;
  };
  mqtt: {
    status: number | null;
    success_count: number | null;
    fail_count: number | null;
    reconnect_count: number | null;
    last_success: number | null;
  };
  sensors: {
    [sensorName: string]: {
      status: number;
      success_count: number;
      fail_count: number;
      success_rate: number;
      last_read: number;
      last_value?: number | null;
    };
  };
  storage: {
    stored_count: number | null;
    errors: number | null;
    unsent?: number | null;
    sent?: number | null;
  };
  // `null` = occupation INCONNUE. Ne jamais rabattre sur 0 : « 0 % occupe »
  // se lit comme une mesure.
  disk?: {
    total: number | null;
    used: number | null;
    free: number | null;
    usage_percent: number | null;
  };
  // Ce que l'API a reellement recu, bloc par bloc.
  donnees_disponibles?: {
    disk: boolean;
    memory: boolean;
    wifi: boolean;
    mqtt: boolean;
    storage: boolean;
    system: boolean;
  };
  errors_total?: number | null;
  recent_errors?: Array<{
    ts: string;
    code: number;
    message: string;
    details: string;
    count: number;
  }>;
}

export interface DiagnosticResponse {
  boitier_id: number;
  last_diagnostic: DiagnosticData | null;
  status: string;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
}

// LOT 3.4 — reponse de GET /users/me. Sert a ne plus PROPOSER ce que l'API
// refusera de toute facon : depuis le LOT 3.1, les endpoints de champs et de
// seuils exigent `write` (`admin` pour la suppression). Sans cet appel,
// l'ecran offre un bouton « Sauvegarder » qui rend 403.
export interface UtilisateurCourant {
  username: string;
  disabled?: boolean;
  permissions?: string[];
}

export interface RincageReading {
  ts: string;
  debit1: number | null;
  debit2: number | null;
  conductivity1: number | null;
  conductivity2: number | null;
  vanne_state?: string;
  relay1?: boolean;
  relay2?: boolean;
  [key: string]: number | string | boolean | null | undefined;
}

export interface RincageStatus {
  id: string;
  name?: string;
  mqtt_topic?: string;
  last_reading: RincageReading | null;
  online: boolean;
  mqtt_connected: boolean;
  sensors_active: boolean;
  last_seen: string | null;
  alerts: string[];
  boitier_id?: number;
  fields?: DeviceField[];
}

export interface RincageHistoryData {
  id: string;
  debit1: HistoryPoint[];
  debit2: HistoryPoint[];
  debit1_total_volume?: HistoryPoint[];
  debit2_total_volume?: HistoryPoint[];
  conductivity1: HistoryPoint[];
  conductivity2: HistoryPoint[];
  [key: string]: HistoryPoint[] | string | undefined;
}

export interface DebitPressionReading {
  ts: string;
  pressure: number | null;
  debit: number | null;
  [key: string]: number | string | boolean | null | undefined;
}

export interface DebitPressionStatus {
  id: string;
  name?: string;
  mqtt_topic?: string;
  last_reading: DebitPressionReading | null;
  online: boolean;
  mqtt_connected: boolean;
  sensors_active: boolean;
  last_seen: string | null;
  alerts: string[];
  boitier_id?: number;
  fields?: DeviceField[];
}

export interface DebitPressionHistoryData {
  id: string;
  pressure: HistoryPoint[];
  debit: HistoryPoint[];
  [key: string]: HistoryPoint[] | string | undefined;
}

// Classe service API
export class ApiService {
  private onAuthChangeCallback: ((authenticated: boolean) => void) | null = null;

  constructor() {
    // Connecter le callback module-level utilisé par l'intercepteur 401
    _onAuthErrorCallback = () => this.notifyAuthChange(false);
  }

  // Enregistrer un callback appelé quand l'état d'authentification change (login/logout/401)
  setOnAuthChange(callback: (authenticated: boolean) => void): void {
    this.onAuthChangeCallback = callback;
  }

  private notifyAuthChange(authenticated: boolean): void {
    if (this.onAuthChangeCallback) {
      this.onAuthChangeCallback(authenticated);
    }
  }

  // LOT 3.4 — profil du porteur du jeton, memorise pour la session.
  // `null` = inconnu (appel jamais fait ou en echec) : tant que /users/me n'a
  // pas repondu, aucun controle n'est applique cote interface ; c'est l'API
  // qui refuse.
  private utilisateurCourant: UtilisateurCourant | null = null;

  async getCurrentUser(forcer: boolean = false): Promise<UtilisateurCourant | null> {
    if (this.utilisateurCourant && !forcer) {
      return this.utilisateurCourant;
    }
    try {
      const response = await api.get('/users/me');
      this.utilisateurCourant = response.data as UtilisateurCourant;
      return this.utilisateurCourant;
    } catch (error) {
      console.error("Erreur lors de la recuperation de l'utilisateur courant:", error);
      return null;
    }
  }

  // Hierarchie identique a celle du serveur (rbac.has_permission) :
  // admin implique write, write implique read.
  static aLaPermission(utilisateur: UtilisateurCourant | null, permission: string): boolean {
    if (!utilisateur || !utilisateur.permissions) {
      // Inconnu : on laisse l'API decider plutot que de bloquer a tort.
      return true;
    }
    const perms = utilisateur.permissions;
    if (perms.includes('admin')) return true;
    if (permission === 'read') return perms.includes('read') || perms.includes('write');
    if (permission === 'write') return perms.includes('write');
    return false;
  }

  // Authentification utilisateur
  async login(credentials: LoginCredentials): Promise<boolean> {
    try {
      // FormData est attendu par l'endpoint /token
      const formData = new URLSearchParams();
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);

      const response = await api.post('/token', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      const data = response.data as TokenResponse;
      
      // Stocker le token et sa date d'expiration (extraite du JWT)
      localStorage.setItem(TOKEN_KEY, data.access_token);
      let expiryTime = new Date();
      try {
        const payload = JSON.parse(atob(data.access_token.split('.')[1]));
        if (payload.exp) {
          expiryTime = new Date(payload.exp * 1000);
        } else {
          expiryTime.setMinutes(expiryTime.getMinutes() + 60);
        }
      } catch {
        expiryTime.setMinutes(expiryTime.getMinutes() + 60);
      }
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toISOString());

      this.notifyAuthChange(true);
      return true;
    } catch (error: any) {
      // 429 = temporisation, PAS un mauvais mot de passe. Distinguer les deux
      // est ce qui empêche l'opérateur d'aggraver son propre verrouillage.
      if (error?.response?.status === 429) {
        this.logout();
        throw new ErreurTropDeTentatives(delaiDeReessai(error));
      }
      console.error('Erreur d\'authentification:', error);
      this.logout();
      return false;
    }
  }

  // Déconnexion
  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    // LOT 3.4 : sans cet oubli, le compte suivant heriterait des permissions
    // du precedent sur le meme onglet.
    this.utilisateurCourant = null;
    this.notifyAuthChange(false);
  }

  // Vérifier si l'utilisateur est connecté
  // Le token peut être expiré : le refresh interceptor le renouvellera automatiquement.
  // On considère l'utilisateur authentifié tant qu'un token existe et n'a pas
  // dépassé la fenêtre max de refresh (7 jours après expiration).
  isAuthenticated(): boolean {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;

    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiry) return true; // Token présent mais pas de date — laisser le refresh gérer

    const expiryDate = new Date(expiry);
    const now = new Date();
    const maxRefreshWindow = 7 * 24 * 60 * 60 * 1000; // 7 jours en ms

    // Déconnecté seulement si le token est expiré depuis plus de 7 jours
    return (now.getTime() - expiryDate.getTime()) < maxRefreshWindow;
  }
  
  // Récupérer le statut de tous les bains
  /**
   * Présence temps réel de tous les boîtiers.
   *
   * LOT 7.4 — `supervision` distingue DEUX ignorances que l'écran confondait :
   *   'ok'           la supervision a interrogé sa base et répond ;
   *   'indisponible' la supervision n'a pas pu répondre (503 côté API).
   *
   * Sans ce champ, une panne totale de MariaDB rendait `boitiers: {}` en 200,
   * et le tableau de bord masquait un boîtier absent exactement comme un
   * boîtier légitimement inconnu. Le champ est optionnel dans le type : une
   * API antérieure au LOT 7 ne l'envoie pas ; le hook traite alors la
   * réponse comme 'ok', puisque l'API a répondu 200.
   */
  async getPresence(): Promise<{
    supervision?: 'ok' | 'indisponible';
    boitiers: Record<string, any>;
    lien_mqtt_serveur: boolean;
    seuils: any;
  }> {
    const response = await api.get('/presence');
    return response.data;
  }

  async getAllBains(): Promise<BoitierStatus[]> {
    try {
            const response = await api.get('/boitiers/etat');
            
      // Vérifier le format de la réponse
      if (response.data) {
        // Si c'est un tableau, le retourner directement
        if (Array.isArray(response.data)) {
                    return response.data;
        }
        // Si c'est un objet avec des clés numériques (comme {"1": {...}, "2": {...}})
        else if (typeof response.data === 'object') {
          // Essayer de convertir en tableau
          const boitiers: BoitierStatus[] = [];
          
          // Si l'objet a une propriété 'bains' qui est un tableau
          if (response.data.bains && Array.isArray(response.data.bains)) {
                        return response.data.bains;
          }
          
          // Sinon, parcourir toutes les clés de l'objet
          Object.entries(response.data).forEach(([key, value]) => {
            if (typeof value === 'object' && value !== null) {
              // Assurer que l'objet est une instance de BoitierStatus
              const boitier = value as BoitierStatus;
              // Ajouter l'ID du boîtier s'il n'est pas déjà présent
              if (!boitier.boitier_id && !isNaN(parseInt(key))) {
                boitier.boitier_id = parseInt(key);
              }
              // Valeurs par défaut si absentes
              if (boitier.mqtt_connected === undefined) {
                boitier.mqtt_connected = false;
              }
              if (boitier.sensors_active === undefined) {
                boitier.sensors_active = false;
              }
              boitiers.push(boitier);
            }
          });
          
                    return boitiers;
        }
      }
      
      // Si aucun des cas ci-dessus ne s'applique, retourner un tableau vide
      console.warn('Format de réponse inattendu pour /bains:', response.data);
      return [];
    } catch (error) {
      console.error('Erreur lors de la récupération des bains:', error);
      // Retourner un tableau vide en cas d'erreur pour éviter les erreurs côté frontend
      return [];
    }
  }

  // Récupérer le statut d'un bain spécifique
  async getBainStatus(boitierId: number): Promise<BoitierStatus> {
    try {
      const response = await api.get(`/boitiers/${boitierId}/etat`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération du bain #${boitierId}:`, error);
      throw error;
    }
  }

  // Récupérer l'historique des données d'un bain
  async getBainHistory(boitierId: number, startTime: Date, endTime?: Date, interval: string = '5m', raw: boolean = false): Promise<HistoryData> {
    try {
            
      const params: any = {
        start: startTime.toISOString(),
        interval,
        raw,
      };
      
      // Ajouter la date de fin si elle est fournie
      if (endTime) {
        params.end = endTime.toISOString();
      }
      
            const response = await api.get(`/boitiers/${boitierId}/history`, { params, timeout: raw ? 60000 : 10000 });
      
            
      // Vérification des données reçues
      if (!response.data) {
        console.error('Aucune donnée reçue de l\'API');
        throw new Error('Aucune donnée reçue');
      }
      
      // Vérifier la structure des données
      const data = response.data;
            
      // Retourner toutes les données dynamiques de l'API
      // L'API peut retourner n'importe quel champ (temp, pH, cond, debit1, debit2, conductivity1, etc.)
      return data as HistoryData;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'historique du bain #${boitierId}:`, error);
      throw error;
    }
  }

  // Récupérer les paramètres d'alerte d'un bain
  async getAlertSettings(boitierId: number): Promise<AlertSettings> {
    try {
      const response = await api.get(`/boitiers/${boitierId}/alert-settings`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération des paramètres d'alerte du bain #${boitierId}:`, error);
      throw error;
    }
  }

  // Mettre à jour les paramètres d'alerte d'un bain
  async updateAlertSettings(boitierId: number, settings: AlertSettings): Promise<void> {
    try {
      await api.put(`/boitiers/${boitierId}/alert-settings`, settings);
    } catch (error) {
      console.error(`Erreur lors de la mise à jour des paramètres d'alerte du bain #${boitierId}:`, error);
      throw error;
    }
  }

  // Récupérer toutes les alertes (avec filtrage optionnel)
  async getAlerts(
    etat?: string | null,
    dateDebut?: string,
    dateFin?: string,
    boitierId?: number
  ): Promise<Alert[]> {
    try {
      const params: any = {};
      
      // Ajouter les paramètres de filtrage s'ils sont fournis
      if (etat !== undefined && etat !== null) {
        params.etat = etat;
      }
      if (dateDebut) {
        params.date_debut = dateDebut;
      }
      if (dateFin) {
        params.date_fin = dateFin;
      }
      if (boitierId) {
        params.boitier_id = boitierId;
      }
      
      const response = await api.get('/alerts/structured', { params });
      
      // Le backend retourne directement un tableau d'objets Alert
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      // Si c'est un objet, le convertir en tableau
      if (response.data && typeof response.data === 'object') {
        return Object.values(response.data).flat() as Alert[];
      }
      
      return [];
    } catch (error) {
      console.error('Erreur lors de la récupération des alertes:', error);
      throw error; // Propager l'erreur pour une meilleure gestion
    }
  }

  // =====================================================
  // CONFIGURATION EMAIL
  // =====================================================

  // Récupérer la configuration email
  async getEmailConfig(): Promise<EmailConfig> {
    try {
      const response = await api.get('/alerts/email-config');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération de la config email:', error);
      throw error;
    }
  }

  // Mettre à jour la configuration email
  async updateEmailConfig(config: Partial<EmailConfig>): Promise<EmailConfig> {
    try {
      const response = await api.put('/alerts/email-config', config);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la config email:', error);
      throw error;
    }
  }

  // Tester la connexion email
  async testEmailConnection(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/alerts/email-config/test');
      return response.data;
    } catch (error) {
      console.error('Erreur lors du test de connexion email:', error);
      throw error;
    }
  }

  // Récupérer les diagnostics d'un bain
  async getBainDiagnostics(boitierId: number): Promise<DiagnosticResponse> {
    try {
      const response = await api.get(`/boitiers/${boitierId}/diagnostics`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération des diagnostics du bain #${boitierId}:`, error);
      throw error;
    }
  }

  // ---- Capteur de niveau ----

  async getNiveauStatus(niveauId: number): Promise<LevelStatus> {
    try {
      const response = await api.get(`/niveau/${niveauId}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération du capteur de niveau #${niveauId}:`, error);
      throw error;
    }
  }

  async getNiveauHistory(niveauId: number, startTime: Date, endTime?: Date, interval: string = '5m', raw: boolean = false): Promise<LevelHistoryData> {
    try {
      const params: any = {
        start: startTime.toISOString(),
        interval,
        raw,
      };
      if (endTime) params.end = endTime.toISOString();
      const response = await api.get(`/niveau/${niveauId}/history`, { params, timeout: raw ? 60000 : 10000 });
      const data = response.data as LevelHistoryData;
      return {
        ...data,
        id: niveauId,
        niveau: data.niveau || [],
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'historique du capteur de niveau #${niveauId}:`, error);
      throw error;
    }
  }

  async getNiveauDiagnostics(niveauId: number): Promise<any> {
    try {
      const response = await api.get(`/niveau/${niveauId}/diagnostics`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération des diagnostics du capteur de niveau #${niveauId}:`, error);
      throw error;
    }
  }

  // ---- Rinçage ----
  async getRincageStatus(rincageId: string): Promise<RincageStatus> {
    try {
      const response = await api.get(`/rincage/${rincageId}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération du statut du boîtier rinçage ${rincageId}:`, error);
      throw error;
    }
  }

  async getRincageHistory(rincageId: string, start: string, end: string): Promise<RincageHistoryData> {
    try {
      const response = await api.get(`/rincage/${rincageId}/history`, {
        params: { start, end }
      });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'historique du boîtier rinçage ${rincageId}:`, error);
      throw error;
    }
  }

  async getRincageDiagnostics(rincageId: string): Promise<any> {
    try {
      const response = await api.get(`/rincage/${rincageId}/diagnostics`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération des diagnostics du boîtier rinçage ${rincageId}:`, error);
      throw error;
    }
  }

  // ---- Débit/Pression ----
  async getDebitPressionStatus(deviceId: string): Promise<DebitPressionStatus> {
    try {
      const response = await api.get(`/debit_pression/${deviceId}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération du statut du boîtier débit/pression ${deviceId}:`, error);
      throw error;
    }
  }

  async getDebitPressionHistory(deviceId: string, start: string, end: string): Promise<DebitPressionHistoryData> {
    try {
      const response = await api.get(`/debit_pression/${deviceId}/history`, {
        params: { start, end }
      });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'historique du boîtier débit/pression ${deviceId}:`, error);
      throw error;
    }
  }

  async getDebitPressionDiagnostics(deviceId: string): Promise<any> {
    try {
      const response = await api.get(`/debit_pression/${deviceId}/diagnostics`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération des diagnostics du boîtier débit/pression ${deviceId}:`, error);
      throw error;
    }
  }
  // ===========================
  //   DEVICES DYNAMIQUES
  // ===========================

  // Récupérer tous les types de devices
  async getDeviceTypes(): Promise<DeviceType[]> {
    try {
      const response = await api.get('/devices/types');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des types de devices:', error);
      return [];
    }
  }

  /** LOT 9 — relit UNE fiche, notamment pour récupérer sa `version` après un
   *  conflit d'écriture sans faire rechargher toute la liste (et sans faire
   *  perdre à l'opérateur la saisie qu'il a dans son formulaire). */
  async getDevice(deviceId: number): Promise<Device | null> {
    try {
      const response = await api.get(`/devices/${deviceId}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération du device ${deviceId}:`, error);
      throw error;
    }
  }

  // Récupérer tous les devices
  async getAllDevices(typeId?: number): Promise<Device[]> {
    try {
      const params = typeId ? { type_id: typeId } : {};
      const response = await api.get('/devices', { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des devices:', error);
      return [];
    }
  }

  // Créer un nouveau device
  async createDevice(data: DeviceCreateRequest): Promise<DeviceCreateResponse | null> {
    try {
      const response = await api.post('/devices', data);
      return response.data;
    } catch (error) {
      // LOT 2 : remonter le `detail` OBJET du 409/400. Sans cette traduction,
      // l'appelant n'a que `error.message` = « Request failed with status
      // code 409 » et le message métier est perdu.
      const conflit = conflitMqttDepuisErreur(error);
      if (conflit) {
        console.error('Conflit d\'identifiant MQTT à la création:', conflit.code, conflit.message);
        throw conflit;
      }
      console.error('Erreur lors de la création du device:', error);
      throw error;
    }
  }

  // Mettre à jour un device
  async updateDevice(deviceId: number, data: DeviceUpdateRequest): Promise<Device | null> {
    try {
      const response = await api.put(`/devices/${deviceId}`, data);
      return response.data;
    } catch (error) {
      // LOT 9 : le verrouillage optimiste rend 409 `version_perimee` (ou 428
      // si le client n'a pas présenté de version). À traiter AVANT le conflit
      // MQTT : les deux sont des 409 porteurs d'un `detail.code`, et se
      // tromper enverrait l'opérateur renommer un boîtier alors qu'il doit
      // simplement rejouer sa saisie.
      const conflitVersion = conflitVersionDepuisErreur(error);
      if (conflitVersion) {
        console.error(
          `Écriture concurrente sur le device ${deviceId}:`,
          conflitVersion.code,
        );
        throw conflitVersion;
      }
      // LOT 2 : idem POST — la réactivation d'un boîtier supprimé dont le nom
      // a été repris remonte en 409 `reactivation_boitier_supprime`.
      const conflit = conflitMqttDepuisErreur(error);
      if (conflit) {
        console.error(`Conflit d'identifiant MQTT sur le device ${deviceId}:`, conflit.code);
        throw conflit;
      }
      console.error(`Erreur lors de la mise à jour du device ${deviceId}:`, error);
      throw error;
    }
  }

  // Supprimer un device
  async deleteDevice(deviceId: number): Promise<boolean> {
    try {
      await api.delete(`/devices/${deviceId}`);
      return true;
    } catch (error) {
      console.error(`Erreur lors de la suppression du device ${deviceId}:`, error);
      throw error;
    }
  }

  // Regénérer les credentials MQTT
  async regenerateDeviceMqtt(deviceId: number): Promise<MqttCredentials | null> {
    try {
      const response = await api.post(`/devices/${deviceId}/regenerate-mqtt`);
      return response.data.mqtt_credentials;
    } catch (error) {
      console.error(`Erreur lors de la regénération MQTT du device ${deviceId}:`, error);
      throw error;
    }
  }

  // Récupérer les seuils d'alerte d'un device
  async getDeviceAlerts(deviceId: number): Promise<DeviceAlertSetting[]> {
    try {
      const response = await api.get(`/devices/${deviceId}/alerts`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération des alertes du device ${deviceId}:`, error);
      return [];
    }
  }

  // Récupérer les champs de capteurs d'un device
  async getDeviceFields(deviceId: number): Promise<SensorField[]> {
    try {
      const response = await api.get(`/devices/${deviceId}/fields`);
      // L'API peut retourner soit un tableau directement, soit { value: [...] }
      const data = response.data;
      return Array.isArray(data) ? data : (data.value || data || []);
    } catch (error) {
      console.error(`Erreur lors de la récupération des champs du device ${deviceId}:`, error);
      return [];
    }
  }

  // Mettre à jour un champ de capteur d'un device
  async updateDeviceField(deviceId: number, fieldCode: string, data: any): Promise<boolean> {
    try {
      await api.put(`/devices/${deviceId}/fields/${fieldCode}`, data);
      return true;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour du champ ${fieldCode}:`, error);
      throw error;
    }
  }

  // Ajouter un champ à un device
  async addDeviceField(deviceId: number, data: any): Promise<boolean> {
    try {
      await api.post(`/devices/${deviceId}/fields`, data);
      return true;
    } catch (error) {
      console.error(`Erreur lors de l'ajout du champ:`, error);
      throw error;
    }
  }

  // ===========================================
  // Méthodes pour la régulation des relais
  // ===========================================

  // Récupérer la configuration des 2 relais d'un boîtier
  async getRelayConfigs(deviceId: number): Promise<any[]> {
    try {
      const response = await api.get(`/boitiers/${deviceId}/relays`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération des configs relais du device ${deviceId}:`, error);
      return [];
    }
  }

  // Récupérer la configuration d'un relais spécifique
  async getRelayConfig(deviceId: number, relayNumber: number): Promise<any | null> {
    try {
      const response = await api.get(`/boitiers/${deviceId}/relays/${relayNumber}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération du relais ${relayNumber}:`, error);
      return null;
    }
  }

  // Mettre à jour la configuration d'un relais
  async updateRelayConfig(deviceId: number, relayNumber: number, config: any): Promise<boolean> {
    try {
      await api.put(`/boitiers/${deviceId}/relays/${relayNumber}`, config);
      return true;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour du relais ${relayNumber}:`, error);
      return false;
    }
  }

  // Changer manuellement l'état d'un relais
  async setRelayState(deviceId: number, relayNumber: number, state: boolean, reason?: string): Promise<boolean> {
    try {
      await api.post(`/boitiers/${deviceId}/relays/${relayNumber}/state`, {
        device_id: deviceId,
        relay_number: relayNumber,
        state: state,
        trigger_reason: reason || 'Commande manuelle'
      });
      return true;
    } catch (error) {
      console.error(`Erreur lors du changement d'état du relais ${relayNumber}:`, error);
      return false;
    }
  }

  // Récupérer l'historique d'un relais
  async getRelayHistory(deviceId: number, relayNumber: number, limit: number = 100): Promise<any[]> {
    try {
      const response = await api.get(`/boitiers/${deviceId}/relays/${relayNumber}/history`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'historique du relais ${relayNumber}:`, error);
      return [];
    }
  }

  // Synchroniser les configs relais vers le boîtier via MQTT
  async syncRelayConfigsToDevice(deviceId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post(`/boitiers/${deviceId}/relays/sync`);
      return { success: true, message: response.data.message };
    } catch (error: any) {
      console.error(`Erreur lors de la synchronisation des relais:`, error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Erreur de synchronisation' 
      };
    }
  }

  // Envoyer une commande forcée de relais via MQTT
  async sendRelayCommand(deviceId: number, relayNumber: number, state: boolean, reason?: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post(`/boitiers/${deviceId}/relays/${relayNumber}/command`, {
        device_id: deviceId,
        relay_number: relayNumber,
        state: state,
        trigger_reason: reason || 'Commande forcée'
      });
      return { success: true, message: response.data.message };
    } catch (error: any) {
      console.error(`Erreur lors de l'envoi de la commande relais:`, error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Erreur d\'envoi de commande' 
      };
    }
  }
}

// ===========================
//   INTERFACES DEVICES
// ===========================

export interface DeviceType {
  id: number;
  code: string;
  name: string;
  description?: string;
  mqtt_topic_prefix: string;
  icon?: string;
  color?: string;
  device_count?: number;
}

export interface Device {
  id: number;
  device_type_id?: number;
  device_type_code?: string;
  device_type_name?: string;
  device_type_icon?: string;
  device_type_color?: string;
  name: string;
  location?: string;
  mqtt_topic?: string;
  mqtt_username?: string;
  mqtt_topic_prefix?: string;
  is_enabled?: boolean;
  status?: string;
  last_seen?: string;
  notes?: string;
  created_at?: string;
  /** LOT 9 — jeton de verrouillage optimiste, à renvoyer tel quel au PUT
   *  suivant. Optionnel côté type : une réponse servie par une API d'avant
   *  le lot n'en porte pas. */
  version?: number;
  updated_at?: string;
  /** À false, le boîtier n'émet plus d'alerte ni de courriel. Il reste
   *  visible et son état reste tenu à jour. Absent des réponses servies par
   *  une API antérieure à la migration 0011. */
  surveillance_active?: boolean;
}

export interface SensorField {
  id: number;
  field_code: string;
  field_name: string;
  unit?: string;
  default_min?: number;
  default_max?: number;
  precision_digits?: number;
  display_order?: number;
  is_alertable?: boolean;
}

export interface DeviceFieldConfig {
  field_code: string;
  field_name: string;
  unit?: string;
  min_value?: number | null;
  max_value?: number | null;
  precision_digits?: number;
  is_alertable?: boolean;
}

export interface DeviceCreateRequest {
  name: string;
  mqtt_topic: string;  // Topic MQTT complet (ex: plant/boitier1/raw)
  mqtt_username?: string;  // Username MQTT (ex: boitier1)
  location?: string;
  notes?: string;
  generate_password?: boolean;
  fields?: DeviceFieldConfig[];
  // LOT 2 : reprise assumée d'un identifiant libéré par une suppression
  // logique. Posé UNIQUEMENT après confirmation explicite de l'exploitant :
  // l'historique InfluxDB de l'ancien boîtier sera rattaché au nouveau.
  reutiliser_identifiant_supprime?: boolean;
}

export interface DeviceCreateResponse {
  device: Device;
  mqtt_credentials: MqttCredentials;
  message: string;
}

export interface DeviceUpdateRequest {
  name?: string;
  location?: string;
  notes?: string;
  is_enabled?: boolean;
  surveillance_active?: boolean;
  /** LOT 9 — version lue au moment de l'ouverture du formulaire. Sans elle,
   *  l'API répond 428 : c'est volontaire, un client qui ne sait pas gérer
   *  les conflits ne doit pas pouvoir écraser une saisie en silence. */
  version?: number;
}

export interface MqttCredentials {
  username: string;
  password: string;
  topic: string;
}

export interface DeviceAlertSetting {
  field_code: string;
  field_name?: string;
  unit?: string;
  min_value?: number;
  max_value?: number;
  is_active: boolean;
}

// ===========================================
// Types pour la régulation des relais
// ===========================================

export interface RelayConfig {
  id?: number;
  device_id: number;
  relay_number: number;
  name: string;
  is_enabled: boolean;
  field_code?: string;
  field_name?: string;
  unit?: string;
  activate_below_setpoint: boolean;
  setpoint?: number;
  hysteresis: number;
  min_on_time: number;
  min_off_time: number;
  max_on_time?: number;
  delay_before_start: number;
  invert_output: boolean;
  current_state: boolean;
  last_state_change?: string;
}

export interface RelayConfigUpdate {
  name?: string;
  is_enabled?: boolean;
  field_code?: string;
  activate_below_setpoint?: boolean;
  setpoint?: number;
  hysteresis?: number;
  min_on_time?: number;
  min_off_time?: number;
  max_on_time?: number;
  delay_before_start?: number;
  invert_output?: boolean;
}

export interface RelayHistoryEntry {
  timestamp: string;
  state: boolean;
  trigger_value?: number;
  trigger_reason?: string;
}

// Configuration email pour les alertes
export interface EmailConfig {
  smtp_server: string;
  smtp_port: number;
  smtp_username: string;
  smtp_password: string;
  smtp_use_tls: boolean;
  sender_email: string;
  recipient_emails: string;
  email_enabled: boolean;
  min_hours_between_emails: number;
}

// Exporter une instance singleton
export const apiService = new ApiService();

// Fonction pour récupérer seulement les alertes actives
export const fetchActiveAlerts = async () => {
  return await apiService.getAlerts('active');
};

// Exporter également la classe pour les tests ou autres utilisations
export default ApiService;

